import {Component, Inject} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DialogBaseComponent} from '../dialog-base/dialog-base.component';
import {MatFormField, MatHint, MatInput, MatInputModule, MatLabel} from '@angular/material/input';
import {MatOptionModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelect} from '@angular/material/select';
import {UIStateEnum} from 'src/app/enum/UIStateEnum';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatButton} from '@angular/material/button';
import {IPauseGame} from "../../../interfaces/IPauseGame";
import {ActiveUserService} from "../../../services/active-user.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {DateService} from "../../../services/date.service";
import {GameService} from "../../../services/game.service";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";
import {IActiveGameInstance} from "../../../interfaces/IActiveGameInstance";

@Component({
  selector: 'app-pause-game-dialog',
  standalone: true,
  imports: [DialogBaseComponent,
    ReactiveFormsModule,
    MatButton,
    MatInput,
    MatLabel,
    MatHint,
    MatFormField,
    MatSelect,
    MatCheckbox,
    MatOptionModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule],
  templateUrl: './pause-game-dialog.component.html',
  styleUrl: './pause-game-dialog.component.css'
})
export class PauseGameDialogComponent {
  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  public pauseGameWhenOption = new FormControl<string | null>(null, [Validators.required]);
  public unpauseGameDateOption = new FormControl<string | null>(null, [Validators.required]);
  public cancelAutoplaysOption = new FormControl<boolean | null>(null, [Validators.required]);
  public pauseIndefinitelyOption = new FormControl<boolean>(false, {nonNullable: true});

  readonly PAUSE_AFTER_DRAW = "after-draw";
  readonly PAUSE_AFTER_WINNER = "after-winner";

  public pauseGameForm = new FormGroup({
    pauseGameWhen: this.pauseGameWhenOption,
    unpauseDate: this.unpauseGameDateOption,
    cancelAutoplays: this.cancelAutoplaysOption,
    pauseIndefinitely: this.pauseIndefinitelyOption
  });

  constructor(private activeUserService: ActiveUserService,
              private snackBarService: SnackbarService,
              private gameService: GameService,
              private matDialogRef: MatDialogRef<PauseGameDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: {
                game: IGameQueryResult,
                draw: IActiveGameInstance | null
              }) {
    this.pauseGameWhenOption.setValue(this.PAUSE_AFTER_DRAW);
    this.cancelAutoplaysOption.setValue(false);
    this.setDateTimePicker();

    // An indefinite pause has no resume date to validate, so the date control stops being required. Without
    // this the form would be permanently invalid whenever the box is ticked and Submit would do nothing.
    this.pauseIndefinitelyOption.valueChanges.subscribe(indefiniteP => {
      this.unpauseGameDateOption.setValidators(indefiniteP ? [] : [Validators.required]);
      this.unpauseGameDateOption.updateValueAndValidity();
    });
  }

  private setDateTimePicker() {
    // Use draw end date if available, otherwise default to 7 days from now
    const currentDrawDate = this.data.draw?.EndedOn
      ? new Date(this.data.draw.EndedOn)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    const year = currentDrawDate.getFullYear();
    const month = String(currentDrawDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(currentDrawDate.getDate()).padStart(2, '0');
    const hours = String(currentDrawDate.getHours()).padStart(2, '0');
    const minutes = String(currentDrawDate.getMinutes()).padStart(2, '0');
    this.unpauseGameDateOption.setValue(`${year}-${month}-${day}T${hours}:${minutes}`);
  }

  submit() {
    this.uiState = UIStateEnum.ShowLoading;

    if (this.pauseGameForm.invalid) {
      this.pauseGameForm.markAllAsTouched();
      this.uiState = UIStateEnum.ShowData;
      return;
    }

    let unpauseDate = this.pauseIndefinitelyOption.value
      ? null
      : new Date(this.unpauseGameDateOption.value!);

    let command: IPauseGame = {
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.data.game.Id,
      PauseWhen: this.pauseGameWhenOption.value!,
      UnpauseDate: unpauseDate,
      CancelAutoplays: this.cancelAutoplaysOption.value!
    }

    this.gameService.pauseGame(command).subscribe({
      next: () => {
        this.matDialogRef.close(true);
        this.snackBarService.openSuccessfulSnackBar('Successfully paused game.');
      }, error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackBarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackBarService.openErrorSnackBar('There was an error pausing the game');
        }
      }
    })
  }
}
